import { Button } from '@fountain/fountain-ui-components';
import { CircularProgress, Typography } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import { Decision } from './DecisionFooter';
import messages from './messages';
import { useStyles } from './styles';

interface BulkDecisionBarProps {
  count: number;
  /** The decision being recorded right now, or null when none is. */
  running: Decision | null;
  onApprove: () => void;
  onReject: () => void;
  onClear: () => void;
}

/**
 * Approve or reject every selected opening at once (Design Requirements §5).
 *
 * ## In the flow, not floating over it
 *
 * Between the toolbar and the table rather than pinned to the viewport. A floating
 * bar covers the last row of a 25-row page, which is a row the reader may be about
 * to tick — and `layout-composition` keeps a contextual action in the region it
 * acts on. The bar appears only while something is selected, so it costs nothing
 * when it is not in use.
 *
 * ## It says the count, and the count is the whole point
 *
 * The reader is about to decide several things at once, so the number is the
 * heading rather than a detail beside the buttons. Announced with `role="status"`:
 * ticking a checkbox produces no other feedback that the count moved.
 *
 * Reject keeps the negative secondary weight it has on a single row —
 * `constraint-anti-pattern-guard` prohibits a destructive action in the primary
 * position, and this one is a rejection multiplied.
 *
 * ## Clear, Reject, Approve — the row's order, not the prototype's
 *
 * A deliberate divergence, decided rather than overlooked. The prototype leads
 * with `Approve all` and ends with `Clear`, and AXHE-5031 lists button order as
 * in scope. Its reason for the prototype's order was that the order matched the
 * row actions' Approve-then-Reject — which was true when it was filed and is not
 * now: `OpeningActionsCell` shipped Reject-then-Approve under AXHE-5025. So
 * following the prototype today would produce the split the ticket's own
 * argument was against, on one screen, between a row and the bar above it.
 *
 * The guard does not settle it either way: it prohibits a destructive action in
 * the *primary* position, and neither order does that.
 *
 * The labels do come from the prototype: "all" is what tells the reader this
 * button is not the one on the row beside it.
 *
 * ## The progress sits on the decision that is running
 *
 * Both buttons are held while either decision is in flight, but only the one
 * being recorded takes the spinner. A spinner on Approve during a rejection
 * reports an approval against rows that are being refused, which is the opposite
 * of what is happening.
 *
 * Either label can be the one the spinner replaces, so both buttons name
 * themselves explicitly — a control whose name is a spinner is anonymous.
 *
 * The two names are not worth the same, though. An approval waits with no dialog
 * over it, because `ApproveOpeningsDialog` closes on confirm, so Approve's name
 * is the only thing a screen reader has to go on. A rejection waits under
 * `RejectOpeningDialog`, which stays open until the run settles, and a MUI modal
 * marks the whole page behind it `aria-hidden` — so Reject's name reaches nobody
 * while Reject is the button spinning. It is here for the day that dialog closes
 * on confirm too, and because a button that names itself only sometimes is worse
 * than one that always does. The gap it does not close is that a bulk rejection
 * announces no progress at all; that belongs on the dialog's own confirm.
 */
export const BulkDecisionBar: React.FC<BulkDecisionBarProps> = ({
  count,
  running,
  onApprove,
  onReject,
  onClear,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const busy = running !== null;

  return (
    <div className={styles.bulkBar}>
      <Typography className={styles.bulkCount} role="status" variant="body2">
        <FormattedMessage {...messages.bulkSelected} values={{ count }} />
      </Typography>

      <div className={styles.bulkActions}>
        {/* Leaves the rows alone, so it is neither of the two decisions and does
            not sit with them. */}
        <Button
          autoWidth
          size="small"
          type="text"
          disabled={busy}
          onClick={onClear}
        >
          <FormattedMessage {...globalMessages.clear} />
        </Button>
        <Button
          autoWidth
          negative
          // `destructiveSubmitting` only while the rejection is the run in
          // flight, as on `DecisionFooter`'s reject confirm; the class says why.
          className={classNames(styles.destructiveButton, {
            [styles.destructiveSubmitting]: running === 'rejected',
          })}
          size="small"
          type="secondary"
          aria-label={intl.formatMessage(messages.bulkRejectAll)}
          disabled={busy}
          onClick={onReject}
        >
          {running === 'rejected' ? (
            <CircularProgress size={16} />
          ) : (
            <FormattedMessage {...messages.bulkRejectAll} />
          )}
        </Button>
        <Button
          autoWidth
          size="small"
          type="primary"
          aria-label={intl.formatMessage(messages.bulkApproveAll)}
          disabled={busy}
          onClick={onApprove}
        >
          {running === 'approved' ? (
            <CircularProgress size={16} />
          ) : (
            <FormattedMessage {...messages.bulkApproveAll} />
          )}
        </Button>
      </div>
    </div>
  );
};
